/**
 * Utility functions for formatting and displaying folder paths
 */

import type { Folder } from '@/hooks/useFolders';

/** Normalize folder paths for FE/BE comparisons. */
export function normalizeFolderPath(path: string): string {
  const trimmed = path.trim().replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/** Backend-parity subtree rule: folder id match or path prefix match. */
export function getSubtreeFolderIdsByPathPrefix(root: Folder, allFolders: Folder[]): Set<number> {
  const ids = new Set<number>();
  const prefix = root.path;
  for (const f of allFolders) {
    if (f.folder_id === root.folder_id || f.path.startsWith(prefix)) {
      ids.add(f.folder_id);
    }
  }
  return ids;
}

/**
 * Format a folder path with smart truncation for display
 *
 * Examples:
 * - "Applications" -> "Applications"
 * - "Applications/Legal Documents" -> "Applications/Legal Documents"
 * - "Applications/Legal Documents/Contracts/2024" -> "Applications/.../2024"
 *
 * @param path - The full folder path (can have leading slash)
 * @returns Formatted path with smart truncation
 */
export function formatFolderPath(path: string | null | undefined): string {
  if (!path) return '';

  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;

  const parts = cleanPath.split('/').filter(p => p.trim().length > 0);

  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]; // Root folder only
  if (parts.length === 2) return `${parts[0]}/${parts[1]}`; // Two levels

  // More than 2 levels: show first/.../last
  return `${parts[0]}/.../${parts[parts.length - 1]}`;
}

/**
 * Build display path from folder tree by walking up to collect folder names
 * This converts slug-based paths to display names
 *
 * @param folderId - The ID of the folder
 * @param folderTree - The folder tree structure
 * @returns Display path using folder names (e.g., "Applications/Legal Documents")
 */
export function buildDisplayPathFromTree(
  folderId: string | null | undefined,
  folderTree: any[] | null
): string | null {
  if (!folderId || !folderTree) return null;

  // Helper to find folder by ID and collect path of names
  const findAndBuildPath = (
    targetId: string,
    folders: any[],
    parentPath: string[] = []
  ): string[] | null => {
    for (const folder of folders) {
      const currentPath = [...parentPath, folder.name];

      if (folder.folder_id.toString() === targetId) {
        return currentPath;
      }

      if (folder.children && folder.children.length > 0) {
        const found = findAndBuildPath(targetId, folder.children, currentPath);
        if (found) return found;
      }
    }
    return null;
  };

  const pathParts = findAndBuildPath(folderId, folderTree);
  return pathParts ? pathParts.join('/') : null;
}

/**
 * Get the destination path for a file being uploaded, including full folder hierarchy
 * This function expects selectedFolderPath to be a display path (with folder names, not slugs)
 *
 * @param selectedFolderPath - The full display path of the folder (using folder names)
 * @param fileRelativePath - The webkitRelativePath of the file (for nested folders)
 * @returns The complete destination path with smart formatting
 */
export function getFileDestinationPath(
  selectedFolderPath: string | null,
  fileRelativePath?: string | null
): string | null {
  // Priority 1: If a folder is selected in the dropdown, always show that as the base
  if (selectedFolderPath) {
    // selectedFolderPath should already be display names, not slugs

    // If file has its own folder structure (from folder upload), append it to the dropdown folder
    if (fileRelativePath && fileRelativePath.includes('/')) {
      const pathParts = fileRelativePath.split('/');
      const pathWithoutFilename = pathParts.slice(0, -1); // Remove filename, keep root folder
      const nestedPath = pathWithoutFilename.length > 0 ? pathWithoutFilename.join('/') : '';

      // Combine full folder path with nested path
      if (nestedPath) {
        const fullPath = `${selectedFolderPath}/${nestedPath}`;
        return formatFolderPath(fullPath);
      }
    }

    // Just return the full folder path
    return formatFolderPath(selectedFolderPath);
  }

  // Priority 2: If no dropdown folder selected but file has folder structure, show that
  if (fileRelativePath && fileRelativePath.includes('/')) {
    const pathParts = fileRelativePath.split('/');
    const pathWithoutFilename = pathParts.slice(0, -1); // Remove filename, keep root folder
    if (pathWithoutFilename.length > 0) {
      return formatFolderPath(pathWithoutFilename.join('/'));
    }
  }

  return null;
}
